/**
 * Sixty-six's deck, deal, ranking, card points, legality, visibility and the
 * running count (docs/rules/sixty-six.md §1–§7).
 */
import { SUITS, cardId, rankOf, suitOf } from '../../core/cards.js';
import { anyOf } from '../../core/legality.js';
import { emptyTrick } from '../../core/trick.js';
import { SIXTY_SIX_LAST_TRICK } from './config.js';
/** Rules §1: 9 to Ace in four suits. Canonical order is fixed forever: the
 *  fairness commitment hashes it. */
export function sixtySixDeck() {
    const cards = [];
    for (const suit of SUITS)
        for (let r = 9; r <= 14; r++)
            cards.push(cardId(r, suit));
    return Object.freeze(cards);
}
/** Rules §2: three to the non-dealer, three to the dealer, the trump card
 *  turned up, three more each. The other eleven are the talon. */
export function sixtySixDealPlan() {
    return { steps: [{ each: 3 }, { kitty: 1 }, { each: 3 }], firstSeatOffset: 1 };
}
// Rules §1: A 10 K Q J 9, in every suit, trump or not.
const ORDER = { 14: 6, 10: 5, 13: 4, 12: 3, 11: 2, 9: 1 };
const POINTS = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2, 9: 0 };
export function sixtySixPoints(card) {
    return POINTS[rankOf(card) ?? 0] ?? 0;
}
export function sixtySixRanking(trump) {
    return {
        trumpSuit: trump,
        effectiveSuit: (card) => suitOf(card),
        isTrump: (card) => trump !== null && suitOf(card) === trump,
        strength: (card) => ORDER[rankOf(card) ?? 0] ?? 0,
    };
}
export const sixtySixOpponent = (seat) => (seat === 0 ? 1 : 0);
export function sixtySixCurrentTrick(hand) {
    return hand.tricks[hand.tricks.length - 1] ?? emptyTrick(0, (hand.dealer + 1) % 2);
}
/** Face-down talon plus the turned-up card under it. */
export function sixtySixTalon(hand) {
    return hand.secret.stock.length + hand.secret.kitty.length;
}
/** Rules §4: from a close, or once the talon is drawn out, the strict rules. */
export function sixtySixStrict(hand) {
    return hand.extra.closed !== null || sixtySixTalon(hand) === 0;
}
/** The marriage declared on the lead to the current trick, if any: the lead
 *  must then be its King or Queen (rules §5.1). */
export function sixtySixLeadMarriage(hand) {
    const t = sixtySixCurrentTrick(hand);
    return hand.extra.marriages.find((m) => m.trick === t.index && t.plays.length === 0)?.suit ?? null;
}
/**
 * Rules §4. While the talon is open any card may be played. Under the strict
 * rules the follower must follow suit and beat the card led if they can; void
 * in it, they must trump if they can; otherwise anything. A leader who has
 * just declared a marriage leads its King or Queen.
 */
export function sixtySixLegalPlays(hand, seat) {
    const holding = hand.secret.holdings[seat] ?? [];
    const trick = sixtySixCurrentTrick(hand);
    if (trick.plays.length === 0) {
        const married = sixtySixLeadMarriage(hand);
        if (married)
            return anyOf(holding.filter((c) => suitOf(c) === married && (rankOf(c) === 12 || rankOf(c) === 13)));
        return anyOf(holding);
    }
    if (!sixtySixStrict(hand))
        return anyOf(holding);
    const ctx = sixtySixRanking(hand.extra.trump);
    const led = trick.plays[0].card;
    const ledSuit = suitOf(led);
    const follow = holding.filter((c) => suitOf(c) === ledSuit);
    if (follow.length > 0) {
        const over = follow.filter((c) => ctx.strength(c) > ctx.strength(led));
        return anyOf(over.length > 0 ? over : follow);
    }
    const trumps = holding.filter((c) => ctx.isTrump(c));
    return anyOf(trumps.length > 0 ? trumps : holding);
}
/**
 * The running count from the public record — the tricks and the declared
 * marriages — so the client and the bots read the same numbers as the score.
 * `playedOut` says the hand ran out of cards (the last-trick ten is due).
 */
export function sixtySixCount(tricks, extra, playedOut) {
    const cards = [0, 0];
    const won = [0, 0];
    const married = [0, 0];
    const last = [0, 0];
    let final = null;
    for (const t of tricks) {
        if (t.winner === null)
            continue;
        won[t.winner] += 1;
        for (const p of t.plays)
            cards[t.winner] += sixtySixPoints(p.card);
        final = t;
    }
    for (const m of extra.marriages)
        married[m.seat] += m.points;
    if (playedOut && extra.closed === null && final && final.winner !== null)
        last[final.winner] = SIXTY_SIX_LAST_TRICK;
    const total = [0, 1].map((s) => cards[s] + last[s] + (won[s] > 0 ? married[s] : 0));
    return { cards, marriages: married, tricks: won, lastTrick: last, total };
}
/** Is every card played? Then the hand is over whatever the count says. */
export function sixtySixAllPlayed(hand) {
    return hand.secret.holdings.every((h) => h.length === 0);
}
export function sixtySixCountOf(hand) {
    return sixtySixCount(hand.tricks, hand.extra, sixtySixAllPlayed(hand));
}
function handOver(hand, matchFinished) {
    return matchFinished || hand.phase === 'handComplete' || hand.phase === 'abandoned';
}
/** Cards a seat is known to hold that it has not played yet. */
export function sixtySixShown(extra, tricks) {
    const played = new Set();
    for (const t of tricks)
        for (const p of t.plays)
            played.add(p.card);
    return extra.shown.filter((s) => !played.has(s.card));
}
/**
 * Your own hand; the other player's at the end. The trump card under the
 * talon is face up for both (and stays known once the talon is closed over
 * it); the talon itself is a count. Cards the table has been shown — a
 * marriage, an exchanged trump card — are `revealed`.
 */
export const sixtySixVisibility = {
    holdings(hand, viewerSeat, matchFinished) {
        const over = handOver(hand, matchFinished);
        return hand.secret.holdings.map((_, seat) => (over || seat === viewerSeat ? 'cards' : 'count'));
    },
    kitty: () => 'cards',
    discards: () => 'hidden',
    stock: () => 'count',
    revealed(hand) {
        const h = hand;
        const held = new Set(h.secret.holdings.flat());
        return sixtySixShown(h.extra, h.tricks).map((s) => s.card).filter((c) => held.has(c));
    },
};
//# sourceMappingURL=rules.js.map